import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import {
  Fragment,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from 'react';
import 'react-toastify/dist/ReactToastify.css';
import type { TypeOptions, ToastOptions, Id } from 'react-toastify/unstyled';
import { toast } from 'react-toastify/unstyled';
import type { SemanticICONS } from 'semantic-ui-react';
import { Button, Loader, Message, Icon } from 'semantic-ui-react';

import s from './toast.scss'; // eslint-disable-line postcss-modules/no-unused-class

export { ToastContainer, toast } from 'react-toastify/unstyled';

// Message types that can be displayed in a toast
export type ToastMessage =
  | string
  | ReactElement
  | Array<string | ReactElement>
  | ReactNode;

export interface ToastAction {
  /**
   * Optional icon rendered before the label. Accepts a Lucide or other
   * React element.
   */
  icon?: ReactElement;
  /**
   * Button label text.
   */
  label: string;
  /**
   * Callback invoked when the action button is clicked.
   */
  onClick: () => void;
}

export interface AnchoreToastOptions extends Omit<ToastOptions, 'icon'> {
  /**
   * Action buttons rendered below the message body. Useful for retry or
   * dismiss actions on persistent toasts.
   */
  actions?: ToastAction[];
  /**
   * Clear all existing toasts before showing this one
   */
  dismissAll?: boolean;
  /**
   * Custom icon name, or false to disable icon
   */
  icon?: SemanticICONS | false;
  /**
   * Notification severity level. Applies color and a default icon.
   *
   * @default 'default'
   * @deprecated Use `type` instead.
   */
  level?: TypeOptions;
  /**
   * The main message body. Supports strings or JSX, or arrays of either.
   */
  message: ToastMessage;
  /**
   * Optional heading text to display above the message body.
   */
  title?: string | ReactElement;
}

export const TOAST_ERROR_MESSAGE_FALLBACK = 'An unknown error occurred';

// Default icon names for each toast severity level
export const toastIconsByType: Record<TypeOptions, SemanticICONS> = {
  default: 'info circle',
  success: 'thumbs up',
  info: 'info circle',
  warning: 'warning circle',
  error: 'warning sign',
} as const;

// Type guard to check if a message is valid (non-empty)
const isValidMessage = (message: unknown): message is ToastMessage => {
  if (typeof message === 'string') {
    return message.trim().length > 0;
  }

  if (Array.isArray(message)) {
    return (
      message.length > 0 &&
      message.every(item => isValidMessage(item) || isValidElement(item))
    );
  }

  return isValidElement(message);
};

// Extract the icon for a given level and options
export const getToastIcon = (
  type: TypeOptions,
  icon?: SemanticICONS | false,
): ReactElement | undefined => {
  if (icon === false) {
    return undefined;
  }

  const iconName = icon || toastIconsByType[type];

  return <Icon className={s.icon} name={iconName} size="large" />;
};

// Process array messages by wrapping React elements in Message.Item
const processArrayMessage = (
  messages: Array<string | ReactElement>,
): Array<string | ReactElement> =>
  messages.map((item, index) =>
    isValidElement(item) ? (
      // eslint-disable-next-line react/no-array-index-key
      <Message.Item key={`toast-item-${index}`}>{item}</Message.Item>
    ) : (
      item
    ),
  );

export const AnchoreToastContent = ({
  actions,
  loading,
  message = '',
  progress,
  title,
}: {
  actions?: ToastAction[];
  /**
   * When true, renders a spinner alongside the title to indicate a
   * pending operation.
   */
  loading?: boolean;
  /**
   * The main message body. Optional — toasts can render with just a title.
   */
  message?: ToastMessage;
  /**
   * Download progress as a 0–1 value. Renders a percentage label in the
   * footer alongside any action buttons.
   */
  progress?: number;
  title?: string | ReactElement;
}) => {
  useStyles(s);

  const processedMessage = Array.isArray(message)
    ? processArrayMessage(message)
    : message;

  const hasFooter = actions?.length || typeof progress === 'number';

  return (
    <div className={s.content}>
      <Message className={classNames(s.message, 'toast')}>
        <Message.Content>
          {title && (
            <Message.Header className={s.title}>
              {loading ? (
                <span className={s.titleWithLoader}>
                  {title}
                  <Loader active inline inverted size="tiny" />
                </span>
              ) : (
                title
              )}
            </Message.Header>
          )}

          {Array.isArray(processedMessage) ? (
            <Message.List className={s.text} items={processedMessage} />
          ) : processedMessage ? (
            <span className={s.text}>{processedMessage}</span>
          ) : null}
        </Message.Content>
      </Message>

      {hasFooter ? (
        <div className={s.footer}>
          <span className={s.progress}>
            {typeof progress === 'number'
              ? `${Math.floor(progress * 100)}%`
              : ''}
          </span>

          {actions?.length ? (
            <div className={s.actions}>
              {actions.map((action, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Fragment key={index}>
                  {index > 0 && <span className={s.actionDivider}>|</span>}

                  <Button
                    className={s.actionButton}
                    onClick={action.onClick}
                    size="tiny"
                  >
                    {action.icon}
                    {action.label}
                  </Button>
                </Fragment>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

function raiseToast(options: AnchoreToastOptions): string | undefined;
function raiseToast(dismissAll: false): undefined;
function raiseToast(errorObj: Error): void;
function raiseToast(
  message: ToastMessage,
  options: Omit<AnchoreToastOptions, 'message'>,
): Id | undefined;
function raiseToast(
  maybeOptionsOrMessage: ToastMessage | AnchoreToastOptions | Error,
  maybeOptions?: Omit<AnchoreToastOptions, 'message'>,
): Id | undefined;

/**
 * Triggers a toast notification with the specified options.
 *
 * This is a wrapper around `react-toastify` to apply our custom styles.
 *
 * @example
 * ```ts
 * // Basic usage
 * raiseToast('Hello world!');
 * raiseToast(<span>Custom JSX content</span>, { toastId: '🍞' });
 *
 * // Options object
 * raiseToast({
 *   message: 'Error occurred',
 *   level: 'error',
 *   title: 'Oops!',
 *   autoClose: 5000,
 *   icon: 'warning sign'
 * });
 *
 * // Dismiss all toasts
 * raiseToast(false);
 * ```
 */
function raiseToast(
  maybeOptionsOrMessage: ToastMessage | AnchoreToastOptions | false | Error,
  maybeOptions?: Omit<AnchoreToastOptions, 'message'>,
): Id | undefined | void {
  // Handle the dismiss-all case
  if (maybeOptionsOrMessage === false) {
    return toast.dismiss();
  }

  // Handle Error objects
  if (maybeOptionsOrMessage instanceof Error) {
    return raiseToast({
      message: maybeOptionsOrMessage.message || TOAST_ERROR_MESSAGE_FALLBACK,
      level: 'error',
      title: 'Error',
    });
  }

  // Handle message passed as the first argument
  if (isValidMessage(maybeOptionsOrMessage)) {
    return raiseToast({
      ...maybeOptions,
      message: maybeOptionsOrMessage,
    });
  }

  const {
    actions,
    message,
    title,
    icon,
    level = 'default',
    dismissAll,
    ...options
  } = maybeOptionsOrMessage;

  // Validate the message
  if (!isValidMessage(message)) {
    return undefined;
  }

  // Check if we should update an existing toast
  const isUpdate =
    options.toastId && toast.isActive(options.toastId) && dismissAll;

  // Clear all toasts if requested (and not updating)
  if (dismissAll && !isUpdate) {
    toast.dismiss();
  }

  // Create the toast content
  const content = (
    <AnchoreToastContent actions={actions} message={message} title={title} />
  );

  // Backwards compatibility for deprecated `level` option
  const type = options.type || level;

  // Prepare options for react-toastify
  const toastifyOptions: ToastOptions = {
    ...options,
    type,
    icon: getToastIcon(type, icon),
    ariaLabel:
      options.ariaLabel || (typeof message === 'string' ? message : undefined),
  };

  // Either update existing toast or create new one
  if (isUpdate) {
    toast.update(options.toastId!, {
      ...toastifyOptions,
      render: content,
    });
    return options.toastId;
  }

  return toast(content, toastifyOptions);
}

export type ToastHookOptions = Omit<
  AnchoreToastOptions,
  'message' | 'level' | 'type'
>;

const makeToastHook =
  (
    type: TypeOptions,
  ): ((
    message: ToastMessage,
    options?: ToastHookOptions,
  ) => string | undefined) =>
  (message, options) =>
    raiseToast({
      ...options,
      message,
      type,
    });

const success = makeToastHook('success');
const info = makeToastHook('info');
const warning = makeToastHook('warning');
const error = makeToastHook('error');

type Toast = typeof raiseToast & {
  /**
   * Convenience method to show a success toast.
   *
   * @example
   * ```ts
   * raiseToast.success('Very nice!');
   * ```
   */
  success: typeof success;
  /**
   * Convenience method to show an info toast.
   *
   * @example
   * ```ts
   * raiseToast.info('Just so you know...');
   * ```
   */
  info: typeof info;
  /**
   * Convenience method to show a warning toast.
   *
   * @example
   * ```ts
   * raiseToast.warning(<span>Warning, live without warning</span>);
   * ```
   */
  warning: typeof warning;
  /**
   * Convenience method to show an error toast.
   *
   * @example
   * ```ts
   * raiseToast.error('Beep boop', { autoClose: false });
   * ```
   */
  error: typeof error;
};

export default Object.assign(raiseToast, {
  success,
  info,
  warning,
  error,
}) as Toast;
